import React from 'react'

import { useToggle } from '@app/hooks'
import { LntSelect } from '@app/components/base'

import { SELECT_DROPDOWN_RENDER_TYPE } from '@app/components/base/Select/constants'
import { TAG_TYPE } from '@app/constants/tag'
import { SIZE } from '@app/constants'

import { DropdownRender } from './DropdownRender'

import type { ReactElement } from 'react'
import type { ExcludedFilterSelectProps } from './types'
import type { LntSelectProps } from '../base/Select/types'

export const ExcludedFilterSelect =
  <ValueType = unknown, QueryResponseResultType = unknown>
  ({
    value,
    defaultIsExcluded,
    paramKeys,
    mode = 'multiple',
    size = SIZE.large,
    placement = 'bottomRight',
    strictValue = true,
    onChange,
    ...props
  }: ExcludedFilterSelectProps<ValueType, QueryResponseResultType>): ReactElement<ExcludedFilterSelectProps<ValueType, QueryResponseResultType>> => {
    const {
      value: isExcludedState,
      setValue: setIsExcludedState
    } = useToggle(defaultIsExcluded)

    const handleChangeExcluded = (isExcluded: boolean): void => {
      if (isExcluded) {
        onChange({
          [paramKeys.excludedKey]: value,
          [paramKeys.key]: undefined
        })
      } else {
        onChange({
          [paramKeys.excludedKey]: undefined,
          [paramKeys.key]: value
        })
      }

      setIsExcludedState(isExcluded)
    }

    const handleChangeSelect: LntSelectProps['onChange'] = (value): void => {
      if (isExcludedState) {
        return onChange({
          [paramKeys.excludedKey]: value,
          [paramKeys.key]: undefined
        })
      }

      onChange({
        [paramKeys.excludedKey]: undefined,
        [paramKeys.key]: value
      })
    }

    const dropdownRender = (menu: React.ReactElement): React.ReactElement => (
      <DropdownRender
        dropdownRenderType={ SELECT_DROPDOWN_RENDER_TYPE.excluded }
        menu={ menu }
        isExcluded={ isExcludedState }
        onChangeExcluded={ handleChangeExcluded }
      />
    )

    return (
      <LntSelect<ValueType, QueryResponseResultType>
        { ...props }
        strictValue
        value={ value }
        mode={ mode }
        size={ size }
        placement={ placement }
        tagRenderType={ isExcludedState ? TAG_TYPE.danger : TAG_TYPE.primary }
        onChange={ handleChangeSelect }
        dropdownRender={ dropdownRender }
      />
    )
  }
